import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { Alert, Badge, Box, Button, CircularProgress, Typography } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
  MAP_SETTINGS,
  fixLeafletIcon,
  getMarkerIcon,
  createCustomClusterIcon,
  partitionByGpsValidity,
} from '../../../../utils/nsirMapConfig';
import { MAP_MARKER_FOCUS_ZOOM, MAP_ROW_MIN_HEIGHT, MARKER_WARN_THRESHOLD } from '../constants';
import NsirMapaMapLegend from './NsirMapaMapLegend';

fixLeafletIcon();

const HIGHLIGHT_CLASS = 'nsir-marker-highlight';

const NsirMapCanvas = ({
  displayNsirs,
  loading,
  fitBoundsKey,
  focusNsirId,
  focusNsirKey,
  highlightNsirId,
  drawerOpen,
  viewportCount,
  onMarkerClick,
  onClusterClick,
  onViewportChange,
  onToggleDrawer,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const markersByIdRef = useRef({});
  const displayNsirsRef = useRef(displayNsirs);
  const callbacksRef = useRef({
    onMarkerClick,
    onClusterClick,
    onViewportChange,
  });

  displayNsirsRef.current = displayNsirs;
  callbacksRef.current = {
    onMarkerClick,
    onClusterClick,
    onViewportChange,
  };

  const emitViewportChange = () => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    const inViewport = displayNsirsRef.current.filter((n) =>
      bounds.contains([n.lat, n.lng])
    );
    callbacksRef.current.onViewportChange?.(inViewport);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, {
      center: MAP_SETTINGS.center,
      zoom: MAP_SETTINGS.initialZoom,
      minZoom: MAP_SETTINGS.minZoom,
      maxZoom: MAP_SETTINGS.maxZoom,
      maxBounds: MAP_SETTINGS.maxBounds,
      maxBoundsViscosity: MAP_SETTINGS.maxBoundsViscosity,
      worldCopyJump: MAP_SETTINGS.worldCopyJump,
      bounceAtZoomLimits: MAP_SETTINGS.bounceAtZoomLimits,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const cluster = L.markerClusterGroup({
      iconCreateFunction: createCustomClusterIcon,
      maxClusterRadius: 70,
      showCoverageOnHover: false,
      chunkedLoading: true,
      chunkInterval: 200,
      chunkDelay: 50,
      spiderfyOnMaxZoom: true,
    });

    cluster.on('clusterclick', (event) => {
      const childMarkers = event.layer.getAllChildMarkers();
      const nsirs = childMarkers
        .map((m) => m.options.nsirData?.nsir)
        .filter(Boolean);
      callbacksRef.current.onClusterClick?.(nsirs, event);
    });

    cluster.addTo(map);
    mapRef.current = map;
    clusterRef.current = cluster;

    map.on('moveend', emitViewportChange);

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      map.off('moveend', emitViewportChange);
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
      markersByIdRef.current = {};
    };
  }, []);

  useEffect(() => {
    const cluster = clusterRef.current;
    const map = mapRef.current;
    if (!cluster || !map || loading) return undefined;

    cluster.clearLayers();
    markersByIdRef.current = {};

    if (displayNsirs.length === 0) {
      map.invalidateSize();
      emitViewportChange();
      return undefined;
    }

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;

      const layers = displayNsirs.map((nsir) => {
        const marker = L.marker([nsir.lat, nsir.lng], {
          icon: getMarkerIcon(nsir),
        });
        marker.options.nsirData = {
          status: nsir.status,
          anomalia: nsir.anomalia,
          id: nsir.id,
          nsir,
        };
        marker.on('click', () => {
          callbacksRef.current.onMarkerClick?.(nsir);
        });
        markersByIdRef.current[String(nsir.id)] = marker;
        return marker;
      });

      cluster.addLayers(layers);
      map.invalidateSize();
      emitViewportChange();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [displayNsirs, loading]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || fitBoundsKey == null || displayNsirs.length === 0) return;

    const bounds = L.latLngBounds(displayNsirs.map((n) => [n.lat, n.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14, animate: true });
    }
  }, [fitBoundsKey, displayNsirs]);

  useEffect(() => {
    Object.entries(markersByIdRef.current).forEach(([id, marker]) => {
      const el = marker.getElement?.();
      if (!el) return;
      if (String(id) === String(highlightNsirId)) {
        el.classList.add(HIGHLIGHT_CLASS);
        el.style.zIndex = '1000';
        el.style.filter = 'drop-shadow(0 0 6px rgba(237, 108, 2, 0.9))';
      } else {
        el.classList.remove(HIGHLIGHT_CLASS);
        el.style.zIndex = '';
        el.style.filter = '';
      }
    });

  }, [highlightNsirId, displayNsirs]);

  useEffect(() => {
    if (!focusNsirKey || !focusNsirId) return;

    const map = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster) return;

    const marker = markersByIdRef.current[String(focusNsirId)];
    const flyToLatLng = (latLng) => {
      const targetZoom = Math.max(map.getZoom(), MAP_MARKER_FOCUS_ZOOM);
      map.flyTo(latLng, targetZoom, { animate: true, duration: 0.75 });
    };

    if (marker) {
      const latLng = marker.getLatLng();
      const flyToMarker = () => flyToLatLng(latLng);
      const visibleParent = cluster.getVisibleParent(marker);
      if (visibleParent && visibleParent !== marker) {
        cluster.zoomToShowLayer(marker, flyToMarker);
      } else {
        flyToMarker();
      }
      return;
    }

    const nsir = displayNsirsRef.current.find(
      (item) => String(item.id) === String(focusNsirId),
    );
    if (nsir?.lat != null && nsir?.lng != null) {
      flyToLatLng([nsir.lat, nsir.lng]);
    }
  }, [focusNsirKey, focusNsirId]);

  const totalNsirs = useMemo(
    () => partitionByGpsValidity(displayNsirs).validas.length,
    [displayNsirs],
  );
  const showPerformanceWarning = !loading && totalNsirs >= MARKER_WARN_THRESHOLD;
  const showEmpty = !loading && displayNsirs.length === 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: MAP_ROW_MIN_HEIGHT,
        minWidth: 0,
        width: '100%',
        flex: 1,
        minWidth: 0,
      }}
    >
      {showPerformanceWarning && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {totalNsirs.toLocaleString('pt-BR')} NSIRs no mapa — refine os filtros para melhorar a performance.
        </Alert>
      )}

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(17,25,39,0.55)' : 'rgba(255,255,255,0.55)'),
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}

      {showEmpty && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
            Nenhuma NSIR encontrada com os filtros atuais.
          </Typography>
        </Box>
      )}

      <Badge
        badgeContent={viewportCount}
        color="primary"
        max={9999}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<FormatListBulletedIcon />}
          onClick={onToggleDrawer}
          sx={{
            boxShadow: 3,
            ...(drawerOpen && {
              bgcolor: 'primary.dark',
            }),
          }}
        >
          Lista
        </Button>
      </Badge>

      <NsirMapaMapLegend />

      <Box
        ref={containerRef}
        sx={{
          height: showPerformanceWarning ? 'calc(100% - 48px)' : '100%',
          width: '100%',
          '& .leaflet-container': {
            height: '100%',
            width: '100%',
            zIndex: 0,
          },
        }}
      />
    </Box>
  );
};

NsirMapCanvas.propTypes = {
  displayNsirs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  fitBoundsKey: PropTypes.number,
  focusNsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  focusNsirKey: PropTypes.number,
  highlightNsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  drawerOpen: PropTypes.bool,
  viewportCount: PropTypes.number,
  onMarkerClick: PropTypes.func,
  onClusterClick: PropTypes.func,
  onViewportChange: PropTypes.func,
  onToggleDrawer: PropTypes.func.isRequired,
};

NsirMapCanvas.defaultProps = {
  fitBoundsKey: 0,
  focusNsirId: null,
  focusNsirKey: 0,
  highlightNsirId: null,
  drawerOpen: false,
  viewportCount: 0,
  onMarkerClick: undefined,
  onClusterClick: undefined,
  onViewportChange: undefined,
};

export default NsirMapCanvas;
